import { useId } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  REORDER_POINT_SOURCE_LABEL,
  REORDER_STATE_LABEL,
  REORDER_STATE_STYLE,
  computeReorder,
  daysOfCover,
  formatReorderNumber,
  reorderTiming,
  reorderPointFormula,
  type ReorderParams,
  type ReorderState,
  type ReorderTiming,
} from '@/lib/reorder'

/**
 * The presentational pieces both re-order calculators share (z8nrz7f7dz) —
 * the standalone one on the Re-Order tab and the material-bound panel in the
 * Re-order dialog and the inventory_unit editor — so the two cannot drift.
 * Every figure comes from src/lib/reorder.ts; nothing is computed here.
 */

export interface CalcInputDef {
  key: string
  label: string
  unit: string
  value: string
  hint?: string
  /** Shown but not typed in: a live figure, e.g. the material's current stock. */
  readOnly?: boolean
}

/** Labelled number inputs in two columns, each with its unit inside the field. */
export function ReorderCalcInputs({
  fields,
  onChange,
  className,
}: {
  fields: CalcInputDef[]
  onChange: (key: string, raw: string) => void
  className?: string
}) {
  return (
    <div className={cn('grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2', className)}>
      {fields.map((f) => (
        <CalcInput key={f.key} def={f} onChange={(raw) => onChange(f.key, raw)} />
      ))}
    </div>
  )
}

function CalcInput({ def, onChange }: { def: CalcInputDef; onChange: (raw: string) => void }) {
  const id = useId()
  return (
    <div className='space-y-1'>
      <label htmlFor={id} className='text-xs font-medium text-slate-600'>
        {def.label}
      </label>
      <div className='relative'>
        <Input
          id={id}
          type='number'
          min={0}
          step='any'
          inputMode='decimal'
          value={def.value}
          readOnly={def.readOnly}
          tabIndex={def.readOnly ? -1 : undefined}
          className={cn('h-9 pr-14 tabular-nums', def.readOnly && 'cursor-default bg-slate-50 text-slate-700 focus-visible:ring-0')}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400'>{def.unit}</span>
      </div>
      {def.hint && <p className='text-[11px] text-slate-500'>{def.hint}</p>}
    </div>
  )
}

export function ReorderDecisionBadge({ state }: { state: ReorderState }) {
  return (
    <span
      className={cn('inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold', REORDER_STATE_STYLE[state])}
      data-testid='reorder-decision'
    >
      {REORDER_STATE_LABEL[state]}
    </span>
  )
}

/**
 * The results: decision, the six figures, and the reorder point worked
 * through with the live numbers so the planner can check it by eye.
 */
export function ReorderCalcResults({
  params,
  currentStock,
  onHand,
  onOrder,
  today = new Date(),
  showDecision = true,
}: {
  params: ReorderParams
  /** Available stock: what the position and the cover are measured on. */
  currentStock: number
  /** On hand, when it differs from available (reserved stock); decides Out of stock. */
  onHand?: number
  onOrder: number
  today?: Date
  showDecision?: boolean
}) {
  const stock = { onHand: onHand ?? currentStock, available: currentStock, onOrder }
  const r = computeReorder(params, stock)
  const cover = daysOfCover(currentStock, params.dailyConsumption)
  const timing = reorderTiming(stock, r.reorderPoint, params.dailyConsumption, today)
  const formula = reorderPointFormula(params, r)

  const tiles: { label: string; value: string; unit?: string; sub?: string; accent?: boolean; tone?: 'late' | 'soon' }[] = [
    { label: 'Total lead time', value: formatReorderNumber(r.totalLeadTimeDays), unit: 'days' },
    { label: 'Safety stock', value: formatReorderNumber(r.safetyStockUnits), unit: 'units' },
    {
      label: 'Reorder point',
      value: formatReorderNumber(r.reorderPoint),
      unit: 'units',
      sub: r.reorderPointSource ? REORDER_POINT_SOURCE_LABEL[r.reorderPointSource] : undefined,
      accent: true,
    },
    { label: 'Order quantity', value: formatReorderNumber(r.suggestedQty), unit: 'units', accent: true },
    { label: 'Days of cover', value: cover === null ? '—' : cover.toFixed(1), unit: 'days' },
    {
      label: 'Reorder by',
      ...reorderByTile(timing),
    },
  ]

  return (
    <div className='space-y-3'>
      {showDecision && (
        <div className='flex items-center justify-between gap-2'>
          <span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Decision</span>
          <ReorderDecisionBadge state={r.state} />
        </div>
      )}
      <dl className='grid grid-cols-2 gap-2 md:grid-cols-3'>
        {tiles.map((t) => (
          <div
            key={t.label}
            className={cn(
              'rounded-lg border px-3 py-2.5',
              t.accent ? 'border-primary/20 bg-primary/[0.04]' : 'border-slate-200 bg-white',
            )}
          >
            <dt className='text-[11px] font-medium uppercase tracking-wide text-slate-500'>{t.label}</dt>
            <dd className='mt-1 flex items-baseline gap-1'>
              <span className={cn('text-lg font-semibold tabular-nums', t.tone === 'late' ? 'text-red-600' : t.tone === 'soon' ? 'text-amber-700' : 'text-slate-900')}>
                {t.value}
              </span>
              {t.unit && t.value !== '—' && <span className='text-xs text-slate-500'>{t.unit}</span>}
            </dd>
            {t.sub && <dd className='text-[11px] text-slate-500'>{t.sub}</dd>}
          </div>
        ))}
      </dl>
      <p className='rounded-md bg-slate-50 px-3 py-2 font-mono text-xs text-slate-600' data-testid='reorder-formula'>
        {formula ?? 'Enter a daily consumption and an order lead time to calculate a reorder point.'}
      </p>
    </div>
  )
}

const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`

/** The Reorder by tile: a date with how far off it is, Today, or Overdue by n days. */
function reorderByTile(t: ReorderTiming | null): { value: string; sub?: string; tone?: 'late' | 'soon' } {
  if (t === null) return { value: '—', sub: 'Needs a daily consumption' }
  if (t.daysFromToday < 0) return { value: 'Overdue', sub: `${plural(-t.daysFromToday, 'day')} late · order today`, tone: 'late' }
  if (t.daysFromToday === 0) return { value: 'Today', sub: fmtDate(t.date), tone: 'soon' }
  return { value: fmtDate(t.date), sub: `in ${plural(t.daysFromToday, 'day')}`, tone: t.daysFromToday <= 7 ? 'soon' : undefined }
}
